import type { Field } from '../../shared/types';
import { sortWardNames } from '../../shared/wards';
import { unitLabel } from '../../shared/slug';
import { STEPS } from '../register/state';

/** A built-in question as the question builder would describe it, for showing what the permission form asks. */
const question = (id: string, type: Field['type'], label: string, required: boolean, extra: Partial<Field> = {}): Field =>
  ({ id: `standard-${id}`, type, label, required, ...(extra.help ? { descriptionEnabled: true } : {}), ...extra });
const yesNo = (id: string, label: string) => question(id, 'choice', label, true, { options: ['Yes', 'No'] });
const followUp = (id: string, label: string) => question(id, 'paragraph', label, true, { help: 'Asked only when the answer above is Yes.' });
// Named in the registration steps as those pages are.
const section = (id: string, label: string, step: number, help: string) => question(id, 'section', label, false, { shortName: STEPS[step], help });

/**
 * The questions every registration asks for the permission form (src/register/Steps.tsx), written as the builder's
 * question types: Yes/No as multiple choice, and questions asked only after certain answers as if always asked.
 */
export function standardQuestions(event: { level: 'stake' | 'ward'; wardNames?: string[] }): Field[] {
  const wards = event.level === 'stake' ? sortWardNames(event.wardNames ?? []) : [];
  return [
    section('contact', 'Contact information', 1, 'The participant and a parent or guardian to call in an emergency.'),
    question('participant', 'title_text', 'Participant', false),
    question('first-name', 'short_text', 'First name', true),
    question('last-name', 'short_text', 'Last name', true),
    ...(wards.length ? [question('ward', 'dropdown', 'Ward', true, { options: wards.map(ward => unitLabel(ward, 'Ward')) })] : []),
    question('attending', 'choice', 'Planning to attend', true, { options: ['Yes', 'No'],
      help: 'Yes unless changed. A No asks only for a message to you, and sends the name with No in the Attending column, skipping the rest.' }),
    question('date-of-birth', 'date', 'Date of birth', true),
    question('telephone', 'short_text', 'Telephone number', true, { help: 'For a participant without a phone, use a parent or guardian’s number.' }),
    question('address', 'short_text', 'Address', true),
    question('city', 'short_text', 'City', true),
    question('state', 'short_text', 'State or province', true),
    question('emergency', 'title_text', 'Emergency contact', false),
    question('guardian', 'short_text', 'Parent or guardian’s full name', true),
    question('primary-phone', 'short_text', 'Primary telephone number', true),
    question('secondary-phone', 'short_text', 'Secondary telephone number', false),
    section('medical', 'Medical information', 2, 'Diet, allergies and medications.'),
    yesNo('diet', 'Does the participant require a special diet?'),
    followUp('diet-explanation', 'Please explain the dietary restrictions'),
    yesNo('allergies', 'Does the participant have any allergies?'),
    followUp('allergy-explanation', 'Please list the allergies'),
    question('medications', 'paragraph', 'List all prescription or over-the-counter (OTC) medications the participant is taking.', false, { help: 'Leave blank if none.' }),
    question('self-administer', 'choice', 'Can the participant self-administer his or her medication?', true, { options: ['Yes', 'No'],
      help: 'Asked only when medications are listed. A No asks the family to contact you directly.' }),
    section('limits', 'Conditions that limit activity', 3, 'Illnesses, surgery, limits and special needs.'),
    yesNo('chronic', 'Does the participant have a chronic or recurring illness?'),
    followUp('chronic-explanation', 'Please explain.'),
    yesNo('surgery', 'Has the participant had surgery or a serious illness in the past year?'),
    followUp('surgery-explanation', 'Please explain.'),
    question('limitations', 'paragraph', 'Identify any other limits, restrictions, or disabilities that could prevent the participant from fully participating in the event or activity.', false, { help: 'Leave blank if none.' }),
    question('special-needs', 'paragraph', 'Other accommodations or special needs', false,
      { help: 'Identify any other needs or considerations the participant has that the event or activity planner should be aware of. Leave blank if none.' }),
  ];
}
